import { ReactElement, useMemo } from 'react'
import { filter } from 'lodash-es'
import { Stack } from '@mui/material'
import CreateElementLabelField from '@editor/pages/StructuralPlanning/components/CreateElementLabelField'
import {
  applyCreateLabel,
  createLabelSchema,
} from '@editor/pages/StructuralPlanning/components/RectangularShape/components/RectangularShapeForm/components/GeometryForm/schema'
import roundPoints from '@editor/utils/roundPoints'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { HotkeyDeleteButton, HotkeySaveButton, SaveButton } from '@ui/actions'
import { Form, ErrorField } from '@ui/forms'
import { useEditElementStore, useModelStore } from '@editorStores'
import FormFields from '../FormFields'
import { columnSchema } from '../schema'

interface Props {
  handleSubmit: (column: PlanLineElement) => void
  handleDelete: () => void
  column: PlanLineElement
  isDeleting?: boolean
  purlin?: ShapeObjectLine
  showCreateLabel?: boolean
}

const GeometryForm = ({
  column,
  handleSubmit,
  handleDelete,
  isDeleting = false,
  purlin = undefined,
  showCreateLabel = false,
}: Props): ReactElement | null => {
  const activeElement = useEditElementStore(state => state.activeElement)
  const { columns } = useModelStore(state => state.model)

  const columnsInStorey = useMemo(() => {
    return filter(columns, ['storey', column.storey])
  }, [columns, column.storey])

  const defaultValues = useMemo(() => {
    const { start, end } = column.shape

    return {
      guid: column.guid,
      shape: {
        start: {
          x: start.x,
          y: start.y,
          z: start.z,
        },
        end: {
          x: end.x,
          y: end.y,
          z: end.z,
        },
      },
      storey: column.storey,
      zStartIsLowerThanEnd: true,
      isIntersecting: false,
      isNotOnPurlin: false,
      ...(showCreateLabel ? { label: '' } : {}),
    }
  }, [column, showCreateLabel])

  const SubmitButton = showCreateLabel ? HotkeySaveButton : SaveButton

  return (
    <Form
      key={`${activeElement}`}
      onSubmit={state => {
        const newState = {
          ...state,
          // the form only edits geometry; the stored plan settings go along unchanged
          positionPlanSettings: column.positionPlanSettings,
          certificateSettings: column.certificateSettings,
          shape: {
            start: roundPoints(
              [new ImmutableVector3(state.shape.start.x, state.shape.start.y, state.shape.start.z)],
              { roundX: 2, roundY: 2 },
            )[0],
            end: roundPoints(
              [new ImmutableVector3(state.shape.end.x, state.shape.end.y, state.shape.end.z)],
              { roundX: 2, roundY: 2 },
            )[0],
          },
        }
        handleSubmit(
          showCreateLabel
            ? applyCreateLabel(newState as PlanLineElement, state.label)
            : (newState as PlanLineElement),
        )
      }}
      validationSchema={showCreateLabel ? columnSchema.concat(createLabelSchema) : columnSchema}
      validationContext={{ columns: columnsInStorey, purlin }}
      defaultValues={defaultValues}
      data-cy="column-geometry-form"
    >
      {({ formState: { isSubmitting } }) => (
        <>
          <Stack direction="column" spacing={1}>
            {showCreateLabel && <CreateElementLabelField />}
            <FormFields />
          </Stack>

          <ErrorField name="zStartIsLowerThanEnd" />
          <ErrorField name="isIntersecting" />
          <ErrorField name="isNotOnPurlin" />
          <Stack direction="row" justifyContent="end" spacing={1} mt={1}>
            <SubmitButton
              type="submit"
              loading={isSubmitting}
              fullWidth
              disabled={isDeleting}
              data-cy="btn-submit"
            >
              Speichern
            </SubmitButton>
            <HotkeyDeleteButton
              onClick={handleDelete}
              size="small"
              fullWidth
              loading={isDeleting}
              disabled={isSubmitting}
              data-cy="delete-column-btn"
            >
              Löschen
            </HotkeyDeleteButton>
          </Stack>
        </>
      )}
    </Form>
  )
}

export default GeometryForm
